/**
 * Features Page - WorkStream
 * Detalhamento dos serviços e funcionalidades
 */
import { ScanFace, Activity, Brain, HardHat, Shield, BarChart3, Users, Bell, Lock, Zap, Check } from 'lucide-react'
import SEO, { breadcrumbSchema } from '../components/SEO'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import CTA from '../components/CTA'

export default function Features() {
  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: 'https://workstream.com.br' },
    { name: 'Features', url: 'https://workstream.com.br/features' },
  ])

  const mainServices = [
    {
      icon: ScanFace,
      title: 'Ponto Facial',
      description: 'Reconhecimento facial com 99.2% de precisão para controle de presença.',
      color: 'cyan',
      features: [
        'Reconhecimento facial com ArcFace',
        'Anti-spoofing (fotos/vídeos)',
        'Geolocalização integrada',
        'Offline mode com sync',
        'Relatórios em tempo real',
        'Integração com folha de ponto',
      ],
    },
    {
      icon: Activity,
      title: 'Análise de Produtividade',
      description: 'Monitore postura, ergonomia e atividades em tempo real.',
      color: 'blue',
      features: [
        'Análise de postura com IA',
        'Detecção de fadiga',
        'Tempo em tarefa',
        'Alertas de pausas',
        'Rankings de performance',
        'Insights acionáveis',
      ],
    },
    {
      icon: Brain,
      title: 'IA Comportamental',
      description: 'Machine learning que aprende e prediz performance.',
      color: 'emerald',
      features: [
        'Predição de turnover',
        'Análise de engajamento',
        'Detecção de anomalias',
        'Recomendações personalizadas',
        'Auto-learning contínuo',
        'Modelos customizáveis',
      ],
    },
    {
      icon: HardHat,
      title: 'Segurança & EPI',
      description: 'Detecção automática de EPIs e compliance com NRs.',
      color: 'orange',
      features: [
        'Detecção de 15+ tipos de EPIs',
        'Compliance com NR-6, NR-35',
        'Alertas em tempo real',
        'Histórico por colaborador',
        'Relatórios de conformidade',
        'Integração SESMT',
      ],
    },
  ]

  const additionalFeatures = [
    {
      icon: Shield,
      title: 'Segurança Enterprise',
      items: ['2FA com TOTP', 'SSO (Google, Microsoft)', 'SAML 2.0', 'Criptografia AES-256'],
    },
    {
      icon: BarChart3,
      title: 'Analytics Avançado',
      items: ['Dashboard em tempo real', 'Exportação PDF/Excel', 'Cohort analysis', 'Custom reports'],
    },
    {
      icon: Users,
      title: 'Gestão de Equipes',
      items: ['Hierarquia de acessos', '5 níveis de permissão', 'Convites por email', 'Bulk operations'],
    },
    {
      icon: Bell,
      title: 'Notificações',
      items: ['Email, SMS, WhatsApp', 'Push notifications', 'Alertas customizáveis', 'Frequency cap'],
    },
    {
      icon: Lock,
      title: 'Compliance LGPD',
      items: ['Consentimento de dados', 'Exportação de dados', 'Direito ao esquecimento', 'Audit logs'],
    },
    {
      icon: Zap,
      title: 'API Pública',
      items: ['REST API completa', 'Webhooks', 'SDK disponível', 'Rate limiting'],
    },
  ]

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400' },
      blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
      emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
      orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400' },
    }
    return colors[color] || colors.cyan
  }

  return (
    <>
      <SEO
        title="Funcionalidades e Recursos"
        description="Conheça todos os recursos do WorkStream: ponto facial, análise de produtividade, IA comportamental e verificação de EPIs. Tudo com visão computacional e machine learning."
        keywords={[
          'ponto facial',
          'reconhecimento facial',
          'análise de produtividade',
          'IA comportamental',
          'verificação de EPIs',
          'compliance NR',
          'machine learning',
        ]}
        url="https://workstream.com.br/features"
        structuredData={breadcrumbs}
      />

      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />

        {/* Header */}
        <header className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent" />
          <div className="absolute inset-0 bg-grid-pattern opacity-30" style={{
            backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.03) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-cyan-400 font-mono text-sm tracking-wider">FUNCIONALIDADES</span>
            <h1 className="text-5xl font-bold text-white mt-4 mb-6">
              Recursos Poderosos
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Visão computacional e IA para transformar a gestão da sua equipe
          </p>
        </div>
      </header>

        {/* Main Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-8">
            {mainServices.map((service) => {
              const Icon = service.icon
              const colors = getColorClasses(service.color)
              
            return (
                <div key={service.title} className="p-8 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-slate-600 transition-all">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                  </div>
                  <p className="text-slate-400 mb-6">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-slate-300">
                        <Check className={`w-4 h-4 ${colors.text} flex-shrink-0`} />
                        {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </section>

        {/* Additional Features */}
        <section className="bg-[#0F172A] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">MAIS RECURSOS</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-4">E muito mais...</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {additionalFeatures.map((feature) => {
                const Icon = feature.icon
                return (
                  <div key={feature.title} className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                        <Icon className="w-5 h-5 text-cyan-400" />
                      </div>
                      <h3 className="font-semibold text-white">{feature.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {feature.items.map((item) => (
                        <li key={item} className="text-sm text-slate-400 flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-cyan-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

      {/* How It Works */}
        <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">COMO FUNCIONA</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-4">Simples e Rápido</h2>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '01', title: 'Cadastre-se', desc: 'Crie sua conta em menos de 2 minutos' },
                { step: '02', title: 'Configure', desc: 'Adicione colaboradores e locais' },
                { step: '03', title: 'Instale', desc: 'Posicione as câmeras nos pontos estratégicos' },
                { step: '04', title: 'Monitore', desc: 'Acompanhe tudo em tempo real' },
              ].map((item) => (
                <div key={item.step} className="text-center p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                  <div className="text-4xl font-bold text-cyan-400 font-mono mb-3">{item.step}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Stats */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
                { label: 'Precisão Facial', value: '99.2%' },
              { label: 'Tempo de Setup', value: '< 5 min' },
                { label: 'Uptime', value: '99.9%' },
              { label: 'Suporte', value: '24/7' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-4xl font-bold text-white mb-2">{stat.value}</p>
                  <p className="text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTA
          title="Pronto para explorar?"
        description="Teste gratuitamente por 14 dias com acesso a todos os recursos."
      />

      <Footer />
    </div>
    </>
  )
}
